import { Button, DataTable, TOUCH } from "@nymbl/nos2-rules";
import { CheckCheck } from "lucide-react";
import { useExpenseApprovals } from "@/hooks/use-expenses";
import { APPROVAL_COLUMNS, formatClaimDate, formatMoney } from "@/lib/expenses/view";
import { withCells } from "@/lib/table-columns";
import type { ExpenseApprovalRow } from "@/schemas/expenses";

const NO_ROWS: ExpenseApprovalRow[] = [];

const COLUMNS = withCells(APPROVAL_COLUMNS, {
  person: { className: "max-w-36" },
  description: {
    className: "max-w-56",
    render: (r) => (
      <>
        {r.description}
        {r.asBackup && <div className="text-xs text-muted-foreground">No other approver</div>}
      </>
    ),
  },
  type: { className: "max-w-40" },
  po: { className: "max-w-48" },
  submitted: { render: (r) => (r.dateSubmitted ? formatClaimDate(r.dateSubmitted) : "") },
  amount: { render: (r) => formatMoney(r.total) },
});

/** Pending claims for the viewer's departments, plus backup cases (spec 3, 7). */
export function ApprovalsTable({ onOpen }: { onOpen: (id: string) => void }) {
  const query = useExpenseApprovals(true);
  return (
    <DataTable
      label="Expenses to approve"
      title="To approve"
      search={{ placeholder: "Search expenses to approve…" }}
      exportName="expenses-to-approve"
      columns={COLUMNS}
      rows={query.data?.rows ?? NO_ROWS}
      rowKey={(r) => r.id}
      loading={query.isPending}
      error={query.isError ? new Error("Expenses didn't load") : undefined}
      onRetry={() => void query.refetch()}
      empty={{ title: "Nothing to approve", icon: CheckCheck }}
      rowActions={(r) => (
        <Button
          variant="outline"
          size="sm"
          className={TOUCH}
          aria-label={`Review ${r.claimantName}: ${r.description}, ${formatMoney(r.total)}`}
          onClick={() => onOpen(r.id)}
        >
          Review
        </Button>
      )}
    />
  );
}
